import agent from "server";
import React from "react";
import { useWindupString } from "windups";
import { GPUInstanceType } from "types/InstanceTypes";
import { ContainerSelected } from "components/Environment/shared/BuildTypes";
import {
  defaultRapidsBaseImage,
  defaultRapidsBaseImageSelection,
} from "components/Environment/Create/ContainerSelector/rapidsBaseImages";
import { getCanonicalGpuName } from "utils/gpuNames";

export const allPublicCloudsName = "Brev Cloud";

export const allowedImageList = [
  { DisplayName: "None", Image: "None" },
  {
    DisplayName: "NVIDIA RAPIDS",
    Image: defaultRapidsBaseImage,
    Description: `NVIDIA RAPIDS ${defaultRapidsBaseImageSelection.rapidsVersion} with Jupyter included`,
  },
  {
    DisplayName: "Pytorch Devel",
    Image: "docker.io/pytorch/pytorch:2.2.0-cuda12.1-cudnn8-devel",
    Description: "Pytorch 2.2.0 with CUDA 12.1 and cuDNN 8 with nvcc",
  },
  {
    DisplayName: "Pytorch Runtime",
    Image: "docker.io/pytorch/pytorch:2.2.0-cuda12.1-cudnn8-runtime",
    Description: "Pytorch 2.2.0 with CUDA 12.1 and cuDNN 8 runtime",
  },
  {
    DisplayName: "NVIDIA NeMo 24.05.01",
    Image: "nvcr.io/nvidia/nemo:24.05.01",
    Description: "NVIDIA NeMo 24.05.01",
  },
  {
    DisplayName: "NVIDIA TensorRT",
    Image: "nvcr.io/nvidia/tensorrt:24.05-py3",
    Description: "NVIDIA TensorRT 24.05 with Python 3",
  },
  {
    DisplayName: "NVIDIA BioNeMo Nightly",
    Image: "nvcr.io/nvidia/clara/bionemo-framework:nightly",
    Description: "BioNeMo Framework Nightly",
  },
  {
    DisplayName: "TensorFlow 2",
    Image: "nvcr.io/nvidia/tensorflow:24.05-tf2-py3",
    Description: "TensorFlow 2 with TensorRT built in",
  },
  {
    DisplayName: "Triton Server",
    Image: "nvcr.io/nvidia/tritonserver:24.06-trtllm-python-py3",
    Description: "Triton Server with TensorRT-LLM built in",
  },
  // {
  //   DisplayName: "NIM-Day0-24.04",
  //   Image: "nvcr.io/ohlfw0olaadg/ea-participants/nim_llm:24.04-day0.rc3",
  //   Description: "24.04-Day0 VLLM and TRT container",
  // },
  // {
  //   DisplayName: "NIM-24.04",
  //   Image: "nvcr.io/ohlfw0olaadg/ea-participants/nim_llm:24.04.rc3",
  //   Description: "24.04 TRT only container",

  // }
];

export const handleIsPaymentSetup = async (
  cloud: string,
  activeOrgId: string,
) => {
  if (!activeOrgId) {
    return false;
  }

  if (cloud === allPublicCloudsName) {
    const res = await agent.Billing.getSavedCreditInfo(activeOrgId);
    if (res.success && res.data) {
      if (res.data.last4 && res.data.expiration) {
        // this means payment is setup
        return true;
      }
    }
    return false;
  } else {
    // We assume the other clouds have the payment set up
    return true;
  }
};

export const LoadingDots: React.FC = () => {
  const dots = new Array(3).fill(".");
  return (
    <div className="flex justify-between w-4">
      {dots.map((dot, i) => (
        <span
          key={i}
          style={{ animation: `dot 1.4s infinite ${i * 0.2}s` }}
          className="opacity-0"
        >
          {dot}
        </span>
      ))}
    </div>
  );
};

export const ConfigurationTitle = ({ isCompressed }) => {
  const [text] = useWindupString(
    isCompressed ? "Configuration selected!" : "Select a configuration:",
  );
  return (
    <h3 className=" text-xl font-medium leading-6 text-gray-900 dark:text-white">
      {text}
    </h3>
  );
};

export function convertToGPUMap(gpuInstances: GPUInstanceType[]): {
  [gpuName: string]: GPUInstanceType[];
} {
  const gpuMap: { [gpuName: string]: GPUInstanceType[] } = {};

  gpuInstances.forEach((instance) => {
    instance.supported_gpus.forEach((gpu) => {
      const canonicalGpuName = getCanonicalGpuName(gpu.name);
      if (gpuMap[canonicalGpuName]) {
        gpuMap[canonicalGpuName].push(instance);
      } else {
        gpuMap[canonicalGpuName] = [instance];
      }
    });
  });

  return gpuMap;
}

export const hasVerbBaseImageContainer = (container?: ContainerSelected) => {
  if (!container) return false;
  if (container.verbBuild?.containerUrl) {
    return container.verbBuild.containerUrl != "Default";
  }
  return false;
};

export const usesVerbYaml = (
  containerUrl: string,
  isCustomContainer: boolean,
) => {
  return !isCustomContainer && containerUrl !== "VM Mode";
};

export const isStoppableInstance = (stoppable?: boolean) => {
  return !!stoppable;
};

/**
 * Formats memory values for better readability in the UI
 * Examples:
 *   "1TiB336GiB" → "1.33 TiB"
 *   "512GiB" → "512 GiB"
 *   "64GB" → "64 GB"
 *   "44GiB720MiB" → "44.70 GiB"
 */
export function formatMemoryForDisplay(
  memory: string | number | undefined,
): string {
  if (memory === undefined) return "";
  // Handle NaN
  if (typeof memory === "number" && isNaN(memory)) return "";
  if (typeof memory === "number") return `${memory} GiB`;

  const str = String(memory);
  const cleanStr = str.trim();

  // Handle empty string
  if (cleanStr === "") {
    return "0 GiB";
  }

  const decimalMemory = cleanStr.match(
    /^(\d+(?:\.\d+)?)\s*(B|KB|MB|GB|TB|PB)$/i,
  );
  if (decimalMemory) {
    return `${decimalMemory[1]} ${decimalMemory[2].toUpperCase()}`;
  }

  let totalGiB = 0;
  let hasUnits = false;

  // Check if the string is ONLY valid memory format (units + numbers, no extra text)
  // Valid formats: "512GiB", "1TiB336GiB", "44GiB720MiB", "119GiB714MiB284KiB", etc.
  const validMemoryFormat =
    /^(\d+(?:\.\d+)?\s*(?:TiB|GiB|MiB|KiB|GB|KB|tib|gib|mib|kib|gb|kb))+$/i;
  const isValidFormat = validMemoryFormat.test(cleanStr);

  if (!isValidFormat) {
    // If it doesn't match valid format, check if it's a plain number
    if (/^[\d.\s]+$/.test(cleanStr)) {
      const parsed = parseFloat(cleanStr);
      if (isNaN(parsed)) {
        return "";
      }
      totalGiB = parsed;
    } else {
      // String contains invalid characters or mixed text
      return "";
    }
  } else {
    // Parse TiB values and convert to GiB (1 TiB = 1024 GiB)
    const tibMatch = str.match(/(\d+(?:\.\d+)?)\s*TiB/i);
    if (tibMatch) {
      totalGiB += parseFloat(tibMatch[1]) * 1024;
      hasUnits = true;
    }

    // Parse GiB values
    const gibMatch = str.match(/(\d+(?:\.\d+)?)\s*GiB/i);
    if (gibMatch) {
      totalGiB += parseFloat(gibMatch[1]);
      hasUnits = true;
    }

    // Parse MiB values and convert to GiB (1 MiB = 1/1024 GiB)
    const mibMatch = str.match(/(\d+(?:\.\d+)?)\s*MiB/i);
    if (mibMatch) {
      totalGiB += parseFloat(mibMatch[1]) / 1024;
      hasUnits = true;
    }

    // Parse KiB values and convert to GiB (1 KiB = 1/(1024^2) GiB)
    const kibMatch = str.match(/(\d+(?:\.\d+)?)\s*KiB/i);
    if (kibMatch) {
      totalGiB += parseFloat(kibMatch[1]) / (1024 * 1024);
      hasUnits = true;
    }

    // Parse GB values (treat as GiB)
    const gbMatch = str.match(/(\d+(?:\.\d+)?)\s*GB/i);
    if (gbMatch) {
      totalGiB += parseFloat(gbMatch[1]);
      hasUnits = true;
    }

    // Parse KB values (treat as KiB)
    const kbMatch = str.match(/(\d+(?:\.\d+)?)\s*KB/i);
    if (kbMatch) {
      totalGiB += parseFloat(kbMatch[1]) / (1024 * 1024);
      hasUnits = true;
    }
  }

  // Helper function to format with up to 2 decimals
  const formatWithDecimals = (value: number, unit: string) => {
    if (value % 1 === 0) return `${value} ${unit}`;
    return `${value.toFixed(2)} ${unit}`;
  };

  // Convert to TiB if >= 1024 GiB, otherwise keep as GiB
  if (totalGiB >= 1024) {
    return formatWithDecimals(totalGiB / 1024, "TiB");
  }

  return formatWithDecimals(totalGiB, "GiB");
}

/**
 * Parses memory values and returns the value in GiB as a number
 * Examples:
 *   "1TiB336GiB" → 1360
 *   "512GiB" → 512
 *   "64GB" → 64
 *   512 → 512
 */
export function parseMemoryToGiB(memory: string | number): number {
  if (typeof memory === "number") return memory;

  const str = String(memory);
  let totalGiB = 0;

  // Match TiB values and convert to GiB (1 TiB = 1024 GiB)
  const tibMatch = str.match(/(\d+(?:\.\d+)?)\s*TiB/i);
  if (tibMatch) {
    totalGiB += parseFloat(tibMatch[1]) * 1024;
  }

  // Match GiB values
  const gibMatch = str.match(/(\d+(?:\.\d+)?)\s*GiB/i);
  if (gibMatch) {
    totalGiB += parseFloat(gibMatch[1]);
  }

  // Match MiB values
  const mibMatch = str.match(/(\d+(?:\.\d+)?)\s*MiB/i);
  if (mibMatch) {
    totalGiB += parseFloat(mibMatch[1]) / 1024;
  }

  // Match KiB values
  const kibMatch = str.match(/(\d+(?:\.\d+)?)\s*KiB/i);
  if (kibMatch) {
    totalGiB += parseFloat(kibMatch[1]) / (1024 * 1024);
  }

  // Match decimal byte units and convert to GiB
  const pbMatch = str.match(/(\d+(?:\.\d+)?)\s*PB/i);
  if (pbMatch) {
    totalGiB += (parseFloat(pbMatch[1]) * 1000 ** 5) / 1024 ** 3;
  }

  const tbMatch = str.match(/(\d+(?:\.\d+)?)\s*TB/i);
  if (tbMatch) {
    totalGiB += (parseFloat(tbMatch[1]) * 1000 ** 4) / 1024 ** 3;
  }

  const gbMatch = str.match(/(\d+(?:\.\d+)?)\s*GB/i);
  if (gbMatch) {
    totalGiB += (parseFloat(gbMatch[1]) * 1000 ** 3) / 1024 ** 3;
  }

  const mbMatch = str.match(/(\d+(?:\.\d+)?)\s*MB/i);
  if (mbMatch) {
    totalGiB += (parseFloat(mbMatch[1]) * 1000 ** 2) / 1024 ** 3;
  }

  const kbMatch = str.match(/(\d+(?:\.\d+)?)\s*KB/i);
  if (kbMatch) {
    totalGiB += (parseFloat(kbMatch[1]) * 1000) / 1024 ** 3;
  }

  const bMatch = str.match(/(\d+(?:\.\d+)?)\s*B/i);
  if (bMatch) {
    totalGiB += parseFloat(bMatch[1]) / 1024 ** 3;
  }

  // If no unit found, assume it's already a number in GiB
  if (totalGiB === 0) {
    totalGiB = parseFloat(str.replace(/[^\d.]/g, "")) || 0;
  }

  return totalGiB;
}
